/*
 * Copyright (c) 2023 NOOGA DEVELOPMENT, All rights reserved.
 * Author Julia Romanovskaya <julia.romanovskaya@noogadev.com>
 */

import { Theme } from '@mui/material/styles';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { useTranslation } from 'react-i18next';
import { MultiSearchSelect } from '../MultiSearchSelect/MultiSearchSelect';
import { ChangeEvent, memo, useState } from 'react';
import { AiActivitiesType } from '../../../data/api/apiCommon.typings';
import {
    Box,
    Checkbox,
    FormControlLabel,
    Slider,
    SliderValueLabelProps,
    Stack,
    Typography,
    styled,
} from '@mui/material';
import { Percent } from '@mui/icons-material';
import { TableTooltip } from 'components/StyledComponents/TableTooltip';
import { makePobabilityText } from '../../RegistryWidgets/Common/AiClassifications/AiClassificationsHistoryConfig';
import { shallowEqual, useSelector } from 'react-redux';
import { selectShortLists } from '../../../data/store-selectors';

export const defaultFromClassificationPercent = 60;
export const defaultToClassificationPercent = 100;
export const defaultDisableProbability = true;

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        probability: {
            margin: theme.spacing(1, 0, 1, 0),
            fontSize: 14,
            fontWeight: 400,
            lineHeight: 1.4,
            color: theme.palette.text.primary,
            opacity: 0.8,
        },
        checkboxWrapper: {
            margin: theme.spacing(0.5, 0, 0, 0),
            '& .MuiFormControlLabel-label': {
                fontSize: 12,
                fontWeight: 400,
                color: theme.palette.text.primary,
                opacity: 0.8,
            },
        },
        checkbox: {
            padding: theme.spacing(0.5),
        },
        slider: {
            marginBottom: theme.spacing(0),
        },
    }),
);

const StylizedSlider = styled(Slider)({
    '& .MuiSlider-thumb': {
        height: 20,
        width: 20,
        backgroundColor: '#fff',
        border: '2px solid currentColor',
        '&:focus, &:hover, &.Mui-active, &.Mui-focusVisible': {
            boxShadow: 'inherit',
        },
        '&:before': {
            display: 'none',
        },
    },
});

function ValueLabelComponent(props: SliderValueLabelProps) {
    const { children, value } = props;

    return (
        <TableTooltip enterTouchDelay={0} placement="top" title={value}>
            {children}
        </TableTooltip>
    );
}

export interface AiClassificationFilterProps {
    selectedActivities: AiActivitiesType[];
    onChangeActivities(value: AiActivitiesType[]): void;
    selectedMinProbability: number;
    selectedMaxProbability: number;
    isDisableProbability: boolean;
    onChangeProbability(min: number, max: number, disable: boolean): void;
    aiActivitySelectLabel?: string;
}

export const AiClassificationFilter = (props: AiClassificationFilterProps) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const {
        selectedActivities,
        onChangeActivities,
        selectedMinProbability,
        selectedMaxProbability,
        isDisableProbability,
        onChangeProbability,
        aiActivitySelectLabel,
    } = props;
    const [isFixedMax100, setIsFixedMax100] = useState<boolean>(selectedMaxProbability === 100 ? true : false);

    const { aiActivitiesList } = useSelector(selectShortLists, shallowEqual);

    const handleChangeSlider = (_event: Event, value: number | number[], _activeThumb: number) => {
        let min = 0;
        let max = 0;
        if (Array.isArray(value)) {
            if (!isFixedMax100) {
                min = value[0];
                max = value[1];
            } else {
                max = 100;
                if (value[0] !== selectedMinProbability) min = value[0];
                else if (value[1] !== selectedMinProbability) min = value[1];
            }
        } else {
            min = value;
            max = value;
        }
        onChangeProbability(min, max, isDisableProbability);
    };

    return (
        <>
            <Box mb={selectedActivities.length === 0 ? 2 : 1}>
                <>
                    <MultiSearchSelect<AiActivitiesType>
                        options={aiActivitiesList}
                        selected={
                            selectedActivities && selectedActivities.length > 0
                                ? aiActivitiesList.filter((item) => selectedActivities.includes(item)).slice(0)
                                : []
                        }
                        onChange={onChangeActivities}
                        getOptionLabel={(option: AiActivitiesType) => t(`domain.aiActivities.${option}`)}
                        label={aiActivitySelectLabel ? aiActivitySelectLabel : t('domain.aiActivity')}
                    />
                    {selectedActivities.length !== 0 && (
                        <FormControlLabel
                            className={classes.checkboxWrapper}
                            label={t('RegistryScreens.ignoreProbability')}
                            control={
                                <Checkbox
                                    className={classes.checkbox}
                                    checked={isDisableProbability}
                                    onChange={(_event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
                                        onChangeProbability(selectedMinProbability, selectedMaxProbability, checked);
                                    }}
                                    size="small"
                                />
                            }
                        />
                    )}
                </>
            </Box>
            {selectedActivities.length !== 0 && !isDisableProbability && (
                <>
                    <Typography className={classes.probability}>
                        {makePobabilityText(selectedMinProbability, selectedMaxProbability, t)}
                    </Typography>
                    <Stack className={classes.slider} spacing={1} direction="row" sx={{ mb: 1 }} alignItems="center">
                        <StylizedSlider
                            getAriaLabel={() => 'Probability range'}
                            value={[selectedMinProbability, selectedMaxProbability]}
                            onChange={handleChangeSlider}
                            valueLabelDisplay="auto"
                            step={1}
                            slots={{ valueLabel: ValueLabelComponent }}
                        />
                        <Percent fontSize="small" />
                    </Stack>
                    <FormControlLabel
                        className={classes.checkboxWrapper}
                        label={t('RegistryScreens.fixMaxValue100')}
                        control={
                            <Checkbox
                                className={classes.checkbox}
                                checked={isFixedMax100}
                                onChange={(_event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
                                    setIsFixedMax100(checked);
                                    if (checked && selectedMaxProbability !== 100)
                                        onChangeProbability(selectedMinProbability, 100, isDisableProbability);
                                }}
                                size="small"
                            />
                        }
                    />
                </>
            )}
        </>
    );
};

export default memo(AiClassificationFilter);
